import { InsurancePlan, InsurancePlanTier, VehiclePlan } from '@/catalog/types';
import { CreditConfig } from './mileage-plan-selection';
import { SubscriptionCFA } from '@/account/types/subscription';
import { getSwapEligibility } from '@/common/utils/swap-state';
import SwapPlanSelectionPanel from './swap-plan-selection-panel';
import CheckoutPlanSelectionPanel, {
  PlanSelectionErrorsConfig,
  PlanSelectionRefs,
} from './checkout-plan-selection-panel';
import useCanOrder from '@/catalog/hooks/use-can-order';
import CannotOrder from './cannot-order';
import { isEmpty } from 'lodash-es';
import CannotOrderPayOverdueBalance, {
  CannotOrderPayOverdueBalanceProps,
} from './cannot-order-pay-overdue-balance';
import useHasUnpaidBalance from '@/account/hooks/use-has-unpaid-balance';

type DelinquencyConfig = Omit<CannotOrderPayOverdueBalanceProps, 'unpaidBalance'> & {
  unpaidBalance?: number;
};

interface PlanSelectionPanelProps {
  // Mileage plans
  vehiclePlans: VehiclePlan[];
  /** Plan mileage in session cart */
  selectedMileagePlan?: number;
  /** Mileage plan on current active subscription */
  currentMileagePlan?: number;
  mostPopularPlanMileageAmount?: string;
  splitPayment?: boolean;
  fullstoryPlanEventDelegate?: () => void;

  // Insurance plans
  insurancePlans?: InsurancePlan[];
  /** Insurance tier in session cart */
  selectedInsuranceTier?: InsurancePlanTier;
  /** Insurance tier on current active subscription */
  currentInsurancePlanTier?: InsurancePlanTier;
  enableInsuranceChange?: boolean;
  fullstoryInsuranceEventDelegate?: () => void;

  // Swaps
  subscription?: SubscriptionCFA;

  // Delinquency
  delinquencyConfig: DelinquencyConfig;

  // Membership
  gasSaverCents: string;
  MEMBERSHIP_COST: string;
  vehicleImagesColumnRef?: React.RefObject<HTMLDivElement | null>;

  // Savings
  savingsAvailable?: boolean;
  estimatedSavings?: number;
  handleSavingsClick?: () => void;
  fullstoryOpenPlanPanelSavingsEventDelegate?: () => void;
  fullstoryOpenMobileStickyFooterSavingsEventDelegate?: () => void;

  // Additional config
  errorsConfig: PlanSelectionErrorsConfig;
  isOrderable: boolean;
  price_savings_1200_over_850?: number;
  price_per_mile_850_plan_for_1200_miles?: number;
  price_difference_850_vs_1200?: number;
  creditConfig: CreditConfig;
  fullstoryContinueEventDelegate?: () => void;
  isSubmittingContinue?: boolean;
  continueButtonRef?: (node?: Element | null) => void;
  planSelectionRefs: PlanSelectionRefs;
  PUBLIC_CDN_URL: string;
}

export default function PlanSelectionPanel({
  // Mileage plans
  vehiclePlans,
  selectedMileagePlan,
  currentMileagePlan,
  mostPopularPlanMileageAmount,
  splitPayment = false,
  fullstoryPlanEventDelegate,

  // Insurance plans
  insurancePlans,
  selectedInsuranceTier,
  currentInsurancePlanTier,
  enableInsuranceChange,
  fullstoryInsuranceEventDelegate,

  // Swaps
  subscription,

  // Delinquency
  delinquencyConfig,

  // Membership
  gasSaverCents,
  MEMBERSHIP_COST,
  vehicleImagesColumnRef,
  // Savings
  savingsAvailable,
  estimatedSavings,
  handleSavingsClick,
  fullstoryOpenPlanPanelSavingsEventDelegate,
  fullstoryOpenMobileStickyFooterSavingsEventDelegate,

  // Additional config
  errorsConfig,
  isOrderable,
  price_savings_1200_over_850,
  price_per_mile_850_plan_for_1200_miles,
  price_difference_850_vs_1200,
  creditConfig,
  fullstoryContinueEventDelegate,
  continueButtonRef,
  planSelectionRefs,
  PUBLIC_CDN_URL,
}: PlanSelectionPanelProps) {
  const hasOutstandingBalance = useHasUnpaidBalance(delinquencyConfig?.unpaidBalance);
  const canOrder = useCanOrder(subscription);
  const { canSwap } = getSwapEligibility(subscription);

  // Check delinquency
  if (hasOutstandingBalance && delinquencyConfig?.unpaidBalance !== undefined) {
    return (
      <CannotOrderPayOverdueBalance
        {...delinquencyConfig}
        unpaidBalance={delinquencyConfig.unpaidBalance}
        PRISTINE_HERO_PDP={true}
      />
    );
  }

  // Check orderability
  if (!canOrder) {
    return (
      <CannotOrder
        hasPendingReturn={!isEmpty(subscription?.upcoming_return)}
        PRISTINE_HERO_PDP={true}
      />
    );
  }

  // Check swap eligibility
  if (canSwap) {
    return (
      <SwapPlanSelectionPanel
        // Mileage plans
        vehiclePlans={vehiclePlans}
        currentMileagePlan={currentMileagePlan}
        mostPopularPlanMileageAmount={mostPopularPlanMileageAmount}
        splitPayment={splitPayment}
        // Insurance plans
        insurancePlans={insurancePlans}
        currentInsurancePlanTier={currentInsurancePlanTier}
        // Savings
        savingsAvailable={savingsAvailable}
        estimatedSavings={estimatedSavings}
        handleSavingsClick={handleSavingsClick}
        fullstoryOpenPlanPanelSavingsEventDelegate={
          fullstoryOpenPlanPanelSavingsEventDelegate
        }
        fullstoryOpenMobileStickyFooterSavingsEventDelegate={
          fullstoryOpenMobileStickyFooterSavingsEventDelegate
        }
        // Additional config
        isOrderable={isOrderable}
        price_savings_1200_over_850={price_savings_1200_over_850}
        price_per_mile_850_plan_for_1200_miles={price_per_mile_850_plan_for_1200_miles}
        price_difference_850_vs_1200={price_difference_850_vs_1200}
        creditConfig={creditConfig}
        fullstoryContinueEventDelegate={fullstoryContinueEventDelegate}
        continueButtonRef={continueButtonRef}
      />
    );
  }

  // Fallback to checkout panel (orderable, non-swap, and non-delinquent customer)
  return (
    <CheckoutPlanSelectionPanel
      // Mileage plans
      vehiclePlans={vehiclePlans}
      selectedMileagePlan={selectedMileagePlan}
      mostPopularPlanMileageAmount={mostPopularPlanMileageAmount}
      splitPayment={splitPayment}
      fullstoryPlanEventDelegate={fullstoryPlanEventDelegate}
      // Insurance plans
      insurancePlans={insurancePlans}
      selectedInsuranceTier={selectedInsuranceTier}
      enableInsuranceChange={enableInsuranceChange}
      fullstoryInsuranceEventDelegate={fullstoryInsuranceEventDelegate}
      // Membership
      gasSaverCents={gasSaverCents}
      MEMBERSHIP_COST={MEMBERSHIP_COST}
      vehicleImagesColumnRef={vehicleImagesColumnRef}
      // Savings
      savingsAvailable={savingsAvailable}
      estimatedSavings={estimatedSavings}
      handleSavingsClick={handleSavingsClick}
      fullstoryOpenPlanPanelSavingsEventDelegate={
        fullstoryOpenPlanPanelSavingsEventDelegate
      }
      fullstoryOpenMobileStickyFooterSavingsEventDelegate={
        fullstoryOpenMobileStickyFooterSavingsEventDelegate
      }
      // Additional config
      errorsConfig={errorsConfig}
      isOrderable={isOrderable}
      price_savings_1200_over_850={price_savings_1200_over_850}
      price_per_mile_850_plan_for_1200_miles={price_per_mile_850_plan_for_1200_miles}
      price_difference_850_vs_1200={price_difference_850_vs_1200}
      creditConfig={creditConfig}
      fullstoryContinueEventDelegate={fullstoryContinueEventDelegate}
      continueButtonRef={continueButtonRef}
      planSelectionRefs={planSelectionRefs}
      PUBLIC_CDN_URL={PUBLIC_CDN_URL}
    />
  );
}
